/**
 * apps/battle/client/src/admin/HealthScreen.tsx — Health tab.
 *
 * Calls GET /api/admin/health on mount + refresh button. Shows:
 *   - DB ping status
 *   - User counts by role (active only)
 *   - Recent failed gradings (last hour, score=0, mark_value>1, latency>0)
 *
 * Plus a "Probe grading provider" button that hits POST /api/admin/health/probe
 * — used right after swapping provider/model in the Knobs tab to confirm the
 * new wiring actually works before students hit it.
 */
import { useEffect, useState } from 'react';
import { api, ApiError } from '../api.js';
import type { HealthResp, ProbeResp } from './api.js';

interface Props {
  showToast: (msg: string, kind?: 'success' | 'error' | 'warn') => void;
}

export function HealthScreen({ showToast }: Props) {
  const [data, setData] = useState<HealthResp | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [probe, setProbe] = useState<ProbeResp | null>(null);
  const [probing, setProbing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadErr(null);
    api
      .get<HealthResp>('/api/admin/health')
      .then((r) => { if (!cancelled) setData(r); })
      .catch((err) => {
        if (cancelled) return;
        const msg = err instanceof ApiError ? `${err.status} ${err.message}` : String(err);
        setLoadErr(msg);
      });
    return () => { cancelled = true; };
  }, [reloadTick]);

  async function runProbe() {
    setProbing(true);
    setProbe(null);
    try {
      const r = await api.post<ProbeResp>('/api/admin/health/probe');
      setProbe(r);
      showToast(`Probe ok — ${r.providerUsed} (${r.latencyMs}ms)`, 'success');
    } catch (err) {
      // The route returns 502 with the ProbeResp body on failure.
      if (err instanceof ApiError && err.body && typeof err.body === 'object') {
        setProbe(err.body as ProbeResp);
      } else {
        setProbe({ ok: false, error: String(err) });
      }
      showToast('Probe failed — check the result panel for detail', 'error');
    } finally {
      setProbing(false);
    }
  }

  return (
    <div data-testid="screen-health">
      <div className="toolbar">
        <button className="ghost sm" onClick={() => setReloadTick((n) => n + 1)} data-testid="button-refresh-health">
          Refresh
        </button>
        <button className="primary sm" onClick={() => void runProbe()} disabled={probing} data-testid="button-probe-grading">
          {probing ? 'Probing…' : 'Probe grading provider'}
        </button>
      </div>

      {loadErr && <div className="err">Failed to load: {loadErr}</div>}
      {!data && !loadErr && <div className="spinner">Loading health…</div>}

      {data && (
        <>
          <div className="card">
            <h2>Overview</h2>
            <div className="stat-grid">
              <div className={`stat-card ${data.db.status === 'ok' ? 'ok' : 'err'}`} data-testid="stat-db">
                <div className="lbl">Database</div>
                <div className="val">{data.db.status}</div>
                {data.db.detail && <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>{data.db.detail}</div>}
              </div>
              <div
                className={`stat-card ${(data.recentFailedGradingsCount ?? 0) === 0 ? 'ok' : 'warn'}`}
                data-testid="stat-failed-gradings"
              >
                <div className="lbl">Failed gradings (1h)</div>
                <div className="val">{data.recentFailedGradingsCount ?? 0}</div>
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>score=0, marks&gt;1, latency&gt;0</div>
              </div>
              {Object.entries(data.userCounts ?? {}).map(([role, count]) => (
                <div className="stat-card" key={role} data-testid={`stat-users-${role}`}>
                  <div className="lbl">Active {role}s</div>
                  <div className="val">{count}</div>
                </div>
              ))}
              <div className="stat-card" data-testid="stat-time">
                <div className="lbl">Server time (UTC)</div>
                <div className="val" style={{ fontSize: 14 }}>{new Date(data.time).toISOString().slice(0, 19).replace('T', ' ')}</div>
              </div>
            </div>
          </div>

          {probe && (
            <div className="card">
              <h2>Latest probe</h2>
              <div className={`probe-result ${probe.ok ? 'ok' : 'err'}`} data-testid="probe-result">
                {probe.ok ? (
                  <>
                    OK · provider={probe.providerUsed} · latency={probe.latencyMs}ms
                  </>
                ) : (
                  <>FAILED · {probe.error}</>
                )}
              </div>
            </div>
          )}

          <div className="card">
            <h2>Recent failed gradings (last hour)</h2>
            {(data.recentFailedGradings ?? []).length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>No failed gradings in the last hour.</p>
            ) : (
              <table className="events-table" data-testid="failed-gradings-table">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Student</th>
                    <th>Template</th>
                    <th>Provider</th>
                    <th>Marks</th>
                    <th>Score</th>
                    <th>Latency (ms)</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recentFailedGradings ?? []).map((e) => (
                    <tr key={e.id}>
                      <td className="mono">{new Date(e.createdAt).toISOString().slice(11, 19)}</td>
                      <td className="mono">{e.studentId.slice(0, 8)}</td>
                      <td className="mono">{e.templateId}</td>
                      <td>{e.providerUsed ?? '—'}</td>
                      <td>{e.markValue}</td>
                      <td>{e.score}</td>
                      <td>{e.markingLatencyMs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
